<script lang="ts" setup>
import { computed, defineProps } from "vue";
import type { TripMonitorItem, TripMonitorRoute } from "@/api/trips/dto/trip-monitor";
import GoogleMapsMixin from "@/mixins/google-maps.mixin";
import { useAppStore } from "@/store/app.store";
import { RouteStatus, RouteStopStatus, type RouteStopStatusValue } from "@/data/trip";
import { RouteRedispatchState } from "@/enums/trip";
import TripTimer from "@/components/trip/TripTimer.vue";
import { useDateTime } from "@/composables";
import dayjs from "dayjs";

const { getPlaceUrlByAddress } = GoogleMapsMixin.methods;
const { dateTZ } = useDateTime();

const appStore = useAppStore();

defineModel<boolean>("showAddCheckCallDialog", { default: false });
defineModel<boolean>("showConfirmDispatchDialog", { default: false });
defineModel<boolean>("showIntervalDialog", { default: false });

const props = defineProps<{
  route: TripMonitorRoute;
  trip: TripMonitorItem;
}>();

const emits = defineEmits<{
  (e: "update:showAddCheckCallDialog", value: boolean): void;
  (e: "update:showConfirmDispatchDialog", value: boolean): void;
  (e: "update:showIntervalDialog", value: boolean): void;
  (e: "setDialogsData", route: TripMonitorRoute, tripId: number): void;
}>();

const isCancelled = computed(() => {
  return props.route.status === RouteStatus.cancelled;
});

const isPlanned = computed(() => {
  return props.route.status === RouteStatus.planned;
});

const buttonDisabled = computed<boolean>(() => {
  if (!props.route.nextRouteStop) {
    return true;
  }

  const disabledStatus: RouteStopStatusValue[] = [
    RouteStopStatus.waitingForLoading,
    RouteStopStatus.waitingForGoodToGo,
  ];

  const isNextStopDisabledStatus = disabledStatus.includes(props.route.nextRouteStop.status);

  return (
    isCancelled.value ||
    isPlanned.value ||
    props.route.redispatch === RouteRedispatchState.needed ||
    isNextStopDisabledStatus
  );
});

const isCheckCallInFuture = computed(() => {
  return dayjs(props.route.lastCheckCall?.checkCallTime).isAfter(dayjs().add(1, "minute"));
});

function openCheckCallDialog() {
  emits("setDialogsData", props.route, props.trip.id);
  emits("update:showAddCheckCallDialog", true);
}

function openDispatchDialogs() {
  emits("setDialogsData", props.route, props.trip.id);
  emits("update:showConfirmDispatchDialog", true);
}

function openIntervalDialog() {
  emits("setDialogsData", props.route, props.trip.id);
  emits("update:showIntervalDialog", true);
}
</script>

<template>
  <div>
    <template v-if="route.dispatchConfirmed">
      <v-chip v-if="isCheckCallInFuture" class="bg-indigo-lighten-2 mb-2" color="white" size="x-small"
        >For Future
      </v-chip>

      <div v-if="props.route.lastCheckCall?.locationAddress" class="d-flex align-center mb-2">
        <div class="text-grey-darken-3" data-qa="last-check-call-location">
          {{ props.route.lastCheckCall?.locationAddress }}
        </div>
        <a :href="getPlaceUrlByAddress(props.route.lastCheckCall?.locationAddress)" class="ml-2" target="_blank">
          <v-icon color="success" icon="mdi-map-marker-radius-outline" size="16"></v-icon>
        </a>
      </div>

      <template v-if="appStore.dispatcher && props.route.lastCheckCall">
        <div class="font-size-12 text-grey-darken-2 font-weight-500 mb-2" data-qa="last-check-call-time1">
          {{ dateTZ(props.route.lastCheckCall.checkCallTime, appStore.dispatcher.timezone) }}
        </div>
        <div class="font-size-12 text-grey-darken-2" data-qa="last-check-call-time2">
          {{ dateTZ(props.route.lastCheckCall.checkCallTime, props.route.lastCheckCall?.timezone) }}
        </div>
      </template>

      <div class="d-flex align-center mt-2">
        <trip-timer
          :disabled="buttonDisabled"
          :interval="route.checkCallInterval"
          :next-update-time="route.nextCheckCallTime"
          :truck-id="route.truck.id"
          :truck-number="route.truck.number"
          @open-dialog="openIntervalDialog"
        ></trip-timer>

        <v-btn
          :disabled="buttonDisabled"
          class="font-size-11 font-weight-600 ml-2"
          color="primary"
          data-qa="check-call-button"
          density="comfortable"
          size="small"
          variant="flat"
          @click="openCheckCallDialog"
        >
          Check call
        </v-btn>
      </div>
    </template>

    <div v-else>
      <span class="font-size-12 text-grey-darken-3">
        Please confirm dispatch to start <br />
        processing the trip
      </span>

      <v-btn
        class="font-weight-600 font-size-11 mt-2 d-block"
        color="primary"
        density="comfortable"
        size="small"
        variant="flat"
        @click="openDispatchDialogs"
      >
        Confirm dispatch
      </v-btn>
    </div>
  </div>
</template>

<style lang="scss" scoped></style>
